import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import api from "@/lib/api";
import SEOHead from "@/components/site/SEOHead";
import DraftBanner from "@/components/site/DraftBanner";
import { breadcrumbs, faqPage } from "@/lib/schema";
import Reveal from "@/components/site/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useEnquiry } from "@/components/site/PublicLayout";
import { trackEvent } from "@/lib/settings";
import SmartMedia from "@/components/site/SmartMedia";
import { hasMedia } from "@/lib/media";
import { CustomSection } from "@/components/site/PropertySections";

export default function SEOLanding() {
  const { slug } = useParams();
  const enquiry = useEnquiry();
  const [page, setPage] = useState(null);
  const [props, setProps] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPage(null); setNotFound(false);
    window.scrollTo({ top: 0, behavior: "instant" });
    api.get(`/public/seo-pages/${slug}`).then((r) => {
      setPage(r.data);
      trackEvent("seo_landing_view", { slug });
    }).catch(() => setNotFound(true));
    api.get("/public/properties").then((r) => setProps(r.data || []));
  }, [slug]);

  if (notFound) {
    return (
      <div className="container-x pt-40 pb-32 text-center">
        <div className="overline">404</div>
        <h1 className="display-2 mt-4">Page not found</h1>
        <Link to="/" className="link-arrow mt-8 inline-flex">Back to home <ArrowRight className="w-4 h-4" /></Link>
      </div>
    );
  }
  if (!page) return <div className="pt-32 pb-32 container-x"><div className="animate-pulse h-96 bg-bone" /></div>;

  const baseSchema = page.schema_json ? page.schema_json : {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: page.h1,
    description: page.meta_description,
  };
  const schema = [
    baseSchema,
    breadcrumbs([{ name: "Home", path: "/" }, { name: page.h1 || page.meta_title, path: `/${page.slug}` }]),
    ...(page.faqs?.length ? [faqPage(page.faqs)] : []),
  ];

  return (
    <>
      <SEOHead title={page.meta_title} description={page.meta_description} keywords={page.keywords} ogImage={page.og_image} schema={schema} />
      <DraftBanner show={page.is_published === false} />

      {/* HERO */}
      <section className="relative pt-44 md:pt-52 pb-20 md:pb-28 overflow-hidden" data-testid="seo-hero">
        {hasMedia(page.hero_image) && (
          <>
            <SmartMedia media={page.hero_image} alt={page.h1} className="absolute inset-0 w-full h-full object-cover" eager />
            <div className="absolute inset-0 bg-ink/60" />
          </>
        )}
        <div className="relative container-x text-ivory">
          <Reveal><div className="overline text-ivory/80">Happy Retreats · Bangalore</div></Reveal>
          <Reveal delay={150}>
            <h1 className="font-display text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight mt-8 max-w-4xl">{page.h1}</h1>
          </Reveal>
          <Reveal delay={300} className="mt-10">
            <button onClick={() => enquiry.open("")} className="bg-ivory text-ink px-8 py-4 text-xs uppercase tracking-[0.22em] hover:bg-clay hover:text-ivory transition-colors" data-testid="seo-cta-top">
              {page.cta_text || "Enquire now"}
            </button>
          </Reveal>
        </div>
      </section>

      {/* CONTENT — legacy simple sections */}
      {(page.content_sections || []).length > 0 && (
        <section className="container-x py-8 md:py-12" data-testid="seo-content">
          <div className="max-w-3xl space-y-16">
            {(page.content_sections || []).map((s, i) => (
              <Reveal key={i} delay={i * 100}>
                <div>
                  <div className="overline">Section · 0{i + 1}</div>
                  <h2 className="display-2 mt-4">{s.title}</h2>
                  <p className="body-lead mt-6 whitespace-pre-line">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* DRAG-AND-DROP LAYOUT SECTIONS */}
      {(page.layout || []).filter((s) => s.enabled !== false).map((sec) => (
        <CustomSection key={sec.id} sec={sec} enquiry={enquiry} slug={slug} />
      ))}

      {/* PROPERTIES STRIP */}
      <section className="bg-bone py-8 md:py-12" data-testid="seo-properties">
        <div className="container-x">
          <Reveal><div className="overline">Our properties</div></Reveal>
          <Reveal delay={100}><h2 className="display-2 mt-6">Pick by feeling.</h2></Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {props.map((p, i) => (
              <Reveal key={p.id} delay={i * 60}>
                <Link to={`/${p.slug}`} className="block group" data-testid={`seo-prop-${p.slug}`}>
                  <div className="aspect-[4/5] overflow-hidden zoom-on-hover relative">
                    <SmartMedia media={p.hero?.image_url} alt={p.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="font-display text-2xl mt-4 group-hover:text-clay transition-colors">{p.name}</div>
                  <div className="text-sm text-graphite font-light">{p.tagline}</div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQS */}
      {page.faqs?.length > 0 && (
        <section className="container-x py-8 md:py-12" data-testid="seo-faqs">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <Reveal><div className="overline">Frequent questions</div></Reveal>
              <Reveal delay={100}><h2 className="display-2 mt-6">Asked & answered.</h2></Reveal>
            </div>
            <div className="lg:col-span-8">
              <Accordion type="single" collapsible className="border-t border-ink/10">
                {page.faqs.map((f, i) => (
                  <AccordionItem key={i} value={`f${i}`} className="border-b border-ink/10">
                    <AccordionTrigger className="py-6 font-display text-xl md:text-2xl hover:no-underline text-left">{f.q}</AccordionTrigger>
                    <AccordionContent className="pb-6 text-sm text-graphite font-light leading-relaxed">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="container-x pb-24 md:pb-32" data-testid="seo-cta">
        <div className="bg-ink text-ivory p-10 md:p-20 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <h2 className="display-2 text-ivory">Begin a conversation.</h2>
          <div className="space-y-6">
            <p className="text-ivory/80 font-light leading-relaxed text-base md:text-lg">A planner will send a tailored property recommendation within 24 hours.</p>
            <button onClick={() => enquiry.open("")} className="bg-ivory text-ink px-8 py-4 text-xs uppercase tracking-[0.22em] hover:bg-clay hover:text-ivory transition-colors" data-testid="seo-cta-btn">Plan a visit</button>
          </div>
        </div>
      </section>
    </>
  );
}
